import {
  ArrowUpRight,
  BellRing,
  CheckCircle2,
  Clock3,
  Copy,
  Download,
  ExternalLink,
  Inbox,
  LogOut,
  Mail,
  MapPin,
  MessageCircle,
  Phone,
  RefreshCw,
  Save,
  Search,
  Settings,
  ShieldCheck,
  SlidersHorizontal,
  Trash2,
  type LucideIcon,
} from 'lucide-react';
import { type FormEvent, useEffect, useMemo, useState } from 'react';

type LeadStatus = 'new' | 'contacted' | 'qualified' | 'converted' | 'closed';
type View = 'enquiries' | 'settings';
type FilterStatus = 'all' | LeadStatus;

type Enquiry = {
  _id: string;
  name: string;
  email: string;
  phone?: string;
  company?: string;
  country?: string;
  state?: string;
  city?: string;
  address?: string;
  message: string;
  source?: string;
  status?: LeadStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  lastContactedAt?: string | null;
};

type Stats = {
  total: number;
  new: number;
  contacted: number;
  qualified: number;
  converted: number;
  closed: number;
};

type SiteSettings = {
  notificationEmail: string;
  whatsappNumber: string;
  emailNotificationsEnabled: boolean;
  whatsappNotificationsEnabled: boolean;
  delivery?: {
    emailConfigured: boolean;
    whatsappConfigured: boolean;
  };
};

type Metric = {
  label: string;
  value: string | number;
  icon: LucideIcon;
};

const statusOptions: Array<{ value: LeadStatus; label: string }> = [
  { value: 'new', label: 'New' },
  { value: 'contacted', label: 'Contacted' },
  { value: 'qualified', label: 'Qualified' },
  { value: 'converted', label: 'Converted' },
  { value: 'closed', label: 'Closed' },
];

const statusStyles: Record<LeadStatus, string> = {
  new: 'bg-[#e8f0ec] text-[#355f55]',
  contacted: 'bg-[#edf0f5] text-[#4f6074]',
  qualified: 'bg-[#f2eee2] text-[#796a42]',
  converted: 'bg-[#e5efe5] text-[#456a48]',
  closed: 'bg-[#efebe8] text-[#78665d]',
};

const initialStats: Stats = {
  total: 0,
  new: 0,
  contacted: 0,
  qualified: 0,
  converted: 0,
  closed: 0,
};

function leadStatus(value?: LeadStatus): LeadStatus {
  return value ?? 'new';
}

function localTime(value?: string | null) {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium', timeStyle: 'short' }).format(date);
}

function whatsappHref(value: string, message = 'Hello from Elatorspace admin.') {
  const number = value.replace(/\D/g, '');
  return number ? `https://wa.me/${number}?text=${encodeURIComponent(message)}` : '#';
}

function locationText(item: Enquiry) {
  return [item.city, item.state, item.country].filter(Boolean).join(', ');
}

async function jsonRequest<T>(url: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...init,
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...init.headers,
    },
  });

  const body = (await response.json().catch(() => ({}))) as Record<string, unknown>;
  if (!response.ok) {
    throw new Error(typeof body.message === 'string' ? body.message : 'Request failed.');
  }
  return body as T;
}

export function AdminPortal() {
  const [session, setSession] = useState<'checking' | 'guest' | 'authenticated'>('checking');
  const [adminEmail, setAdminEmail] = useState('elatorspace.info@gmail.com');
  const [loginEmail, setLoginEmail] = useState('elatorspace.info@gmail.com');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [loginBusy, setLoginBusy] = useState(false);

  const [view, setView] = useState<View>('enquiries');
  const [items, setItems] = useState<Enquiry[]>([]);
  const [stats, setStats] = useState<Stats>(initialStats);
  const [selected, setSelected] = useState<Enquiry | null>(null);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterStatus>('all');
  const [leadBusy, setLeadBusy] = useState(false);
  const [leadError, setLeadError] = useState('');
  const [leadSaved, setLeadSaved] = useState(false);

  const [settings, setSettings] = useState<SiteSettings>({
    notificationEmail: 'elatorspace.info@gmail.com',
    whatsappNumber: '919448786778',
    emailNotificationsEnabled: true,
    whatsappNotificationsEnabled: false,
  });
  const [settingsBusy, setSettingsBusy] = useState(false);
  const [settingsError, setSettingsError] = useState('');
  const [settingsSaved, setSettingsSaved] = useState(false);

  const conversionRate = useMemo(
    () => (stats.total > 0 ? Math.round((stats.converted / stats.total) * 100) : 0),
    [stats],
  );

  const metrics: Metric[] = [
    { label: 'Total leads', value: stats.total, icon: Inbox },
    { label: 'New', value: stats.new, icon: Clock3 },
    { label: 'Active pipeline', value: stats.new + stats.contacted + stats.qualified, icon: SlidersHorizontal },
    { label: 'Converted', value: stats.converted, icon: CheckCircle2 },
    { label: 'Conversion', value: `${conversionRate}%`, icon: ArrowUpRight },
  ];

  useEffect(() => {
    void checkSession();
  }, []);

  async function checkSession() {
    try {
      const result = await jsonRequest<{ authenticated: boolean; email: string }>('/api/admin/session');
      setAdminEmail(result.email);
      setLoginEmail(result.email);
      setSession('authenticated');
      await Promise.all([loadEnquiries('', 'all'), loadSettings()]);
    } catch (error) {
      setSession('guest');
      if (error instanceof Error && !error.message.toLowerCase().includes('authentication required')) {
        setLoginError(error.message);
      }
    }
  }

  async function login(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setLoginBusy(true);
    setLoginError('');
    try {
      const result = await jsonRequest<{ authenticated: boolean; email: string }>('/api/admin/login', {
        method: 'POST',
        body: JSON.stringify({ email: loginEmail, password: loginPassword }),
      });
      setAdminEmail(result.email);
      setLoginPassword('');
      setSession('authenticated');
      await Promise.all([loadEnquiries('', 'all'), loadSettings()]);
    } catch (error) {
      setLoginError(error instanceof Error ? error.message : 'Could not sign in.');
    } finally {
      setLoginBusy(false);
    }
  }

  async function logout() {
    await jsonRequest('/api/admin/logout', { method: 'POST' }).catch(() => undefined);
    setSession('guest');
    setItems([]);
    setSelected(null);
  }

  async function loadEnquiries(query = search, status: FilterStatus = filter) {
    setLeadBusy(true);
    setLeadError('');
    try {
      const params = new URLSearchParams({ status, limit: '150' });
      if (query.trim()) params.set('search', query.trim());
      const result = await jsonRequest<{ items: Enquiry[]; stats: Stats }>(`/api/admin/enquiries?${params.toString()}`);
      setItems(result.items);
      setStats(result.stats);
      setSelected((current) => {
        if (result.items.length === 0) return null;
        if (!current) return result.items[0] ?? null;
        return result.items.find((item) => item._id === current._id) ?? result.items[0] ?? null;
      });
    } catch (error) {
      setLeadError(error instanceof Error ? error.message : 'Could not load enquiries.');
    } finally {
      setLeadBusy(false);
    }
  }

  async function saveLead() {
    if (!selected) return;
    setLeadBusy(true);
    setLeadSaved(false);
    setLeadError('');
    try {
      const updated = await jsonRequest<Enquiry>(`/api/admin/enquiries/${selected._id}`, {
        method: 'PATCH',
        body: JSON.stringify({ status: leadStatus(selected.status), notes: selected.notes ?? '' }),
      });
      setSelected(updated);
      setItems((current) => current.map((item) => (item._id === updated._id ? updated : item)));
      setLeadSaved(true);
      window.setTimeout(() => setLeadSaved(false), 2000);
      await loadEnquiries(search, filter);
    } catch (error) {
      setLeadError(error instanceof Error ? error.message : 'Could not save enquiry.');
    } finally {
      setLeadBusy(false);
    }
  }

  async function deleteLead() {
    if (!selected) return;
    const confirmed = window.confirm(`Delete the enquiry from ${selected.name}? This permanently removes it from MongoDB and cannot be undone.`);
    if (!confirmed) return;

    setLeadBusy(true);
    setLeadError('');
    try {
      await jsonRequest<{ deleted: boolean; id: string }>(`/api/admin/enquiries/${selected._id}`, { method: 'DELETE' });
      setSelected(null);
      await loadEnquiries(search, filter);
    } catch (error) {
      setLeadError(error instanceof Error ? error.message : 'Could not delete enquiry.');
    } finally {
      setLeadBusy(false);
    }
  }

  async function loadSettings() {
    setSettingsError('');
    try {
      setSettings(await jsonRequest<SiteSettings>('/api/admin/settings'));
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : 'Could not load settings.');
    }
  }

  async function saveSettings(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSettingsBusy(true);
    setSettingsSaved(false);
    setSettingsError('');
    try {
      const result = await jsonRequest<SiteSettings>('/api/admin/settings', {
        method: 'PUT',
        body: JSON.stringify({
          notificationEmail: settings.notificationEmail,
          whatsappNumber: settings.whatsappNumber,
          emailNotificationsEnabled: settings.emailNotificationsEnabled,
          whatsappNotificationsEnabled: settings.whatsappNotificationsEnabled,
        }),
      });
      setSettings((current) => ({ ...current, ...result }));
      setSettingsSaved(true);
      window.setTimeout(() => setSettingsSaved(false), 2000);
    } catch (error) {
      setSettingsError(error instanceof Error ? error.message : 'Could not save settings.');
    } finally {
      setSettingsBusy(false);
    }
  }

  async function copyEmail() {
    if (!selected || !navigator.clipboard) return;
    await navigator.clipboard.writeText(selected.email).catch(() => undefined);
  }

  if (session === 'checking') {
    return (
      <main className="grid min-h-screen place-items-center bg-[#f4f1e9] text-[#2d3b35]">
        <p className="inline-flex items-center gap-2 text-sm text-[#6d7872]">
          <RefreshCw size={16} className="animate-spin" /> Checking admin session…
        </p>
      </main>
    );
  }

  if (session === 'guest') {
    return (
      <main className="soft-grid min-h-screen bg-[#f4f1e9] px-4 py-8 text-[#2d3b35] sm:px-6">
        <div className="mx-auto grid min-h-[calc(100vh-4rem)] max-w-6xl overflow-hidden rounded-[2rem] border border-[#33423c]/10 bg-[#fffdf8] shadow-[0_35px_100px_rgba(50,64,57,.10)] lg:grid-cols-[.9fr_1.1fr]">
          <section className="hidden bg-[#345b52] p-10 text-white lg:flex lg:flex-col lg:justify-between">
            <img src="/brand/elatorspace-logo.svg" alt="Elatorspace" className="h-11 w-auto self-start brightness-0 invert" />
            <div>
              <p className="text-xs font-semibold uppercase tracking-[.2em] text-white/55">Internal workspace</p>
              <h1 className="mt-5 max-w-md text-5xl font-medium leading-[1.02] tracking-[-.05em]">Manage opportunities without spreadsheet chaos.</h1>
              <p className="mt-6 max-w-md text-sm leading-7 text-white/60">Review enquiries, track the pipeline, keep notes, export data and control lead-notification destinations.</p>
            </div>
            <p className="text-xs text-white/40">Elatorspace Admin · Restricted access</p>
          </section>

          <section className="flex items-center p-6 sm:p-10 lg:p-14">
            <form onSubmit={login} className="mx-auto w-full max-w-md">
              <img src="/brand/elatorspace-logo.svg" alt="Elatorspace" className="mb-10 h-10 w-auto lg:hidden" />
              <span className="grid size-11 place-items-center rounded-2xl bg-[#e5ece7] text-[#355f55]"><ShieldCheck size={20} /></span>
              <h2 className="mt-6 text-3xl font-medium tracking-[-.04em]">Admin sign in</h2>
              <p className="mt-3 text-sm leading-6 text-[#74807a]">Use the credentials configured securely on the server.</p>
              <div className="mt-8 grid gap-4">
                <label className="form-label">Email<input className="field-input" type="email" value={loginEmail} onChange={(event) => setLoginEmail(event.target.value)} required autoComplete="username" /></label>
                <label className="form-label">Password<input className="field-input" type="password" value={loginPassword} onChange={(event) => setLoginPassword(event.target.value)} required autoComplete="current-password" /></label>
                {loginError && <p className="rounded-xl bg-[#f4e9e6] px-4 py-3 text-sm text-[#8b4c43]">{loginError}</p>}
                <button type="submit" disabled={loginBusy} className="btn-primary mt-2 w-full disabled:opacity-60">{loginBusy ? 'Signing in…' : 'Sign in'} <ArrowUpRight size={15} /></button>
              </div>
              <a href="/" className="mt-6 inline-flex items-center gap-2 text-xs font-medium text-[#64706a]">Return to website <ExternalLink size={13} /></a>
            </form>
          </section>
        </div>
      </main>
    );
  }

  return (
    <div className="min-h-screen bg-[#f4f1e9] text-[#2d3b35]">
      <header className="sticky top-0 z-40 border-b border-[#33423c]/8 bg-[#f8f5ed]/90 px-4 py-3 backdrop-blur-xl sm:px-6 lg:px-8">
        <div className="mx-auto flex max-w-[1500px] items-center justify-between gap-4">
          <div className="flex min-w-0 items-center gap-4">
            <a href="/"><img src="/brand/elatorspace-logo.svg" alt="Elatorspace" className="h-8 w-auto sm:h-9" /></a>
            <div className="hidden border-l border-[#33423c]/10 pl-4 sm:block"><p className="text-xs font-semibold uppercase tracking-[.16em] text-[#7a847f]">Admin console</p><p className="mt-0.5 text-xs text-[#52615a]">{adminEmail}</p></div>
          </div>
          <div className="flex gap-2"><a href="/" className="hidden items-center gap-2 rounded-xl border border-[#33423c]/10 bg-white px-3 py-2 text-xs font-semibold shadow-sm sm:inline-flex">Website <ExternalLink size={13} /></a><button type="button" onClick={() => void logout()} className="grid size-9 place-items-center rounded-xl border border-[#33423c]/10 bg-white shadow-sm" aria-label="Sign out"><LogOut size={15} /></button></div>
        </div>
      </header>

      <main className="mx-auto max-w-[1500px] px-4 py-7 sm:px-6 lg:px-8">
        <div className="mb-7 flex flex-col gap-5 lg:flex-row lg:items-end lg:justify-between">
          <div><p className="text-xs font-semibold uppercase tracking-[.18em] text-[#668076]">Operations</p><h1 className="mt-2 text-3xl font-medium tracking-[-.04em] sm:text-4xl">Website enquiries</h1><p className="mt-2 text-sm text-[#74807a]">Timestamps display in your browser's local timezone.</p></div>
          <div className="inline-flex w-fit rounded-2xl border border-[#33423c]/9 bg-white p-1 shadow-sm"><button type="button" onClick={() => setView('enquiries')} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold ${view === 'enquiries' ? 'bg-[#345b52] text-white' : 'text-[#66716c]'}`}><Inbox size={14} /> Enquiries</button><button type="button" onClick={() => setView('settings')} className={`inline-flex items-center gap-2 rounded-xl px-4 py-2.5 text-xs font-semibold ${view === 'settings' ? 'bg-[#345b52] text-white' : 'text-[#66716c]'}`}><Settings size={14} /> Settings</button></div>
        </div>

        {view === 'enquiries' ? (
          <>
            <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-5">
              {metrics.map(({ label, value, icon: Icon }) => <div key={label} className="rounded-2xl border border-[#33423c]/9 bg-white p-5 shadow-sm"><div className="flex items-center justify-between"><p className="text-xs font-semibold uppercase tracking-[.12em] text-[#7a847f]">{label}</p><Icon size={15} className="text-[#6b8178]" /></div><p className="mt-5 text-3xl font-medium tracking-[-.045em]">{value}</p></div>)}
            </section>

            <form onSubmit={(event) => { event.preventDefault(); void loadEnquiries(search, filter); }} className="mt-5 flex flex-col gap-3 rounded-2xl border border-[#33423c]/9 bg-white p-4 shadow-sm lg:flex-row">
              <div className="relative flex-1"><Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8a938e]" /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search name, email, mobile, company, location or message" className="w-full rounded-xl border border-[#33423c]/10 bg-[#f8f5ed] py-2.5 pl-10 pr-4 text-sm outline-none" /></div>
              <select value={filter} onChange={(event) => { const next = event.target.value as FilterStatus; setFilter(next); void loadEnquiries(search, next); }} className="rounded-xl border border-[#33423c]/10 bg-[#f8f5ed] px-4 py-2.5 text-sm"><option value="all">All statuses</option>{statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select>
              <button type="submit" className="inline-flex items-center justify-center gap-2 rounded-xl border border-[#33423c]/10 bg-[#f8f5ed] px-4 text-xs font-semibold"><RefreshCw size={14} className={leadBusy ? 'animate-spin' : ''} /> Refresh</button>
              <a href="/api/admin/enquiries/export.csv" className="inline-flex items-center justify-center gap-2 rounded-xl bg-[#345b52] px-4 py-2.5 text-xs font-semibold text-white"><Download size={14} /> CSV</a>
            </form>

            {leadError && <p className="mt-4 rounded-xl bg-[#f4e9e6] px-4 py-3 text-sm text-[#8b4c43]">{leadError}</p>}

            <section className="mt-5 grid gap-5 xl:grid-cols-[1.1fr_.9fr]">
              <div className="max-h-[760px] overflow-y-auto rounded-2xl border border-[#33423c]/9 bg-white shadow-sm">
                {items.length === 0 && !leadBusy ? <p className="p-12 text-center text-sm text-[#77827c]">No enquiries match this view.</p> : items.map((item) => {
                  const currentStatus = leadStatus(item.status);
                  return <button key={item._id} type="button" onClick={() => setSelected(item)} className={`grid w-full gap-4 border-b border-[#33423c]/7 px-5 py-5 text-left transition sm:grid-cols-[1fr_auto] ${selected?._id === item._id ? 'bg-[#edf2ee]' : 'hover:bg-[#f8f6ef]'}`}><div className="min-w-0"><div className="flex flex-wrap items-center gap-2"><strong className="truncate text-sm">{item.name}</strong><span className={`rounded-full px-2.5 py-1 text-[.62rem] font-semibold uppercase tracking-[.08em] ${statusStyles[currentStatus]}`}>{currentStatus}</span></div><p className="mt-1 truncate text-xs text-[#74807a]">{item.company || item.email}{item.phone ? ` · ${item.phone}` : ''}</p><p className="mt-3 line-clamp-2 text-sm leading-6 text-[#66716c]">{item.message}</p></div><p className="inline-flex items-center gap-1.5 text-xs text-[#7e8883]"><Clock3 size={12} /> {localTime(item.createdAt)}</p></button>;
                })}
              </div>

              <aside className="xl:sticky xl:top-24 xl:self-start">
                {selected ? <div className="rounded-2xl border border-[#33423c]/9 bg-white p-6 shadow-sm">
                  <div className="flex items-start justify-between gap-4"><div><p className="text-xs font-semibold uppercase tracking-[.14em] text-[#84908a]">Lead detail</p><h2 className="mt-2 text-2xl font-medium tracking-[-.035em]">{selected.name}</h2><p className="mt-1 text-sm text-[#74807a]">{selected.company || 'No company provided'}</p></div><span className={`rounded-full px-3 py-1.5 text-xs font-semibold ${statusStyles[leadStatus(selected.status)]}`}>{leadStatus(selected.status)}</span></div>
                  <div className="mt-6 grid gap-3 rounded-xl bg-[#f6f3eb] p-4 text-xs sm:grid-cols-2"><div><p className="text-[#929b96]">Received</p><p className="mt-1 font-medium">{localTime(selected.createdAt)}</p></div><div><p className="text-[#929b96]">Updated</p><p className="mt-1 font-medium">{localTime(selected.updatedAt)}</p></div></div>
                  <div className="mt-5 flex flex-wrap gap-2">
                    <a href={`mailto:${selected.email}`} className="inline-flex items-center gap-2 rounded-xl border border-[#33423c]/10 bg-[#f8f5ed] px-3 py-2 text-xs font-semibold"><Mail size={14} /> Email</a>
                    <button type="button" onClick={() => void copyEmail()} className="inline-flex items-center gap-2 rounded-xl border border-[#33423c]/10 bg-[#f8f5ed] px-3 py-2 text-xs font-semibold"><Copy size={14} /> Copy email</button>
                    {selected.phone && <a href={`tel:${selected.phone.replace(/[^+\d]/g, '')}`} className="inline-flex items-center gap-2 rounded-xl border border-[#33423c]/10 bg-[#f8f5ed] px-3 py-2 text-xs font-semibold"><Phone size={14} /> Call</a>}
                    {selected.phone && <a href={whatsappHref(selected.phone, `Hello ${selected.name}, this is Elatorspace regarding your website enquiry.`)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-xl border border-[#33423c]/10 bg-[#f8f5ed] px-3 py-2 text-xs font-semibold"><MessageCircle size={14} /> WhatsApp</a>}
                  </div>
                  <div className="mt-5 grid gap-3 rounded-xl border border-[#33423c]/8 p-4 text-xs sm:grid-cols-2">
                    <div><p className="text-[#929b96]">Email</p><p className="mt-1 break-all font-medium">{selected.email}</p></div>
                    <div><p className="text-[#929b96]">Mobile</p><p className="mt-1 font-medium">{selected.phone || 'Not provided'}</p></div>
                    <div className="sm:col-span-2"><p className="text-[#929b96]">Location</p><p className="mt-1 inline-flex items-start gap-1.5 font-medium"><MapPin size={13} className="mt-0.5 shrink-0" /> {locationText(selected) || 'Not provided'}</p></div>
                    {selected.address && <div className="sm:col-span-2"><p className="text-[#929b96]">Address</p><p className="mt-1 whitespace-pre-wrap font-medium">{selected.address}</p></div>}
                  </div>
                  <div className="mt-6"><p className="text-xs font-semibold uppercase tracking-[.12em] text-[#89928e]">Enquiry</p><p className="mt-3 whitespace-pre-wrap text-sm leading-7 text-[#56645d]">{selected.message}</p></div>
                  <div className="mt-6 grid gap-4">
                    <label className="form-label">Status<select className="field-input" value={leadStatus(selected.status)} onChange={(event) => setSelected((current) => current ? { ...current, status: event.target.value as LeadStatus } : null)}>{statusOptions.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}</select></label>
                    <label className="form-label">Internal notes<textarea className="field-input min-h-32 resize-y" value={selected.notes ?? ''} onChange={(event) => setSelected((current) => current ? { ...current, notes: event.target.value } : null)} /></label>
                    {selected.lastContactedAt && <p className="text-xs text-[#7b8580]">Last contacted: {localTime(selected.lastContactedAt)}</p>}
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between"><p className="text-xs text-[#557f73]">{leadSaved ? 'Changes saved.' : ''}</p><div className="flex flex-wrap gap-2"><button type="button" onClick={() => void deleteLead()} disabled={leadBusy} className="inline-flex items-center gap-2 rounded-xl border border-[#b66a61]/25 bg-[#fbf1ef] px-4 py-2.5 text-xs font-semibold text-[#9a4f45] disabled:opacity-60"><Trash2 size={14} /> Delete</button><button type="button" onClick={() => void saveLead()} disabled={leadBusy} className="btn-primary disabled:opacity-60"><Save size={14} /> Save lead</button></div></div>
                  </div>
                </div> : <div className="rounded-2xl border border-[#33423c]/9 bg-white p-10 text-center text-sm text-[#77827c]">Select an enquiry to view details.</div>}
              </aside>
            </section>
          </>
        ) : (
          <section className="grid gap-5 xl:grid-cols-[1fr_380px]">
            <form onSubmit={saveSettings} className="rounded-2xl border border-[#33423c]/9 bg-white p-6 shadow-sm sm:p-8">
              <p className="text-xs font-semibold uppercase tracking-[.18em] text-[#668076]">Lead notifications</p>
              <h2 className="mt-2 text-2xl font-medium tracking-[-.035em]">Choose how new enquiries reach you.</h2>
              <p className="mt-3 max-w-2xl text-sm leading-7 text-[#74807a]">Keep automatic notifications on or off independently. You can also use the WhatsApp shortcut manually without configuring the WhatsApp API.</p>

              <div className="mt-8 grid max-w-2xl gap-5">
                <label className="form-label">Notification email<input className="field-input" type="email" required value={settings.notificationEmail} onChange={(event) => setSettings((current) => ({ ...current, notificationEmail: event.target.value }))} /></label>
                <label className="form-label">WhatsApp destination<input className="field-input" required inputMode="tel" value={settings.whatsappNumber} onChange={(event) => setSettings((current) => ({ ...current, whatsappNumber: event.target.value }))} /><span className="text-xs font-normal text-[#87918c]">Use international format, for example 919448786778.</span></label>

                <div className="grid gap-3 sm:grid-cols-2">
                  <button type="button" aria-pressed={settings.emailNotificationsEnabled} onClick={() => setSettings((current) => ({ ...current, emailNotificationsEnabled: !current.emailNotificationsEnabled }))} className={`rounded-2xl border p-4 text-left transition ${settings.emailNotificationsEnabled ? 'border-[#557f73]/35 bg-[#edf3ef]' : 'border-[#33423c]/10 bg-[#f8f5ed]'}`}>
                    <div className="flex items-center justify-between gap-3"><span className="inline-flex items-center gap-2 text-sm font-semibold"><Mail size={15} /> Email notifications</span><span className={`rounded-full px-2.5 py-1 text-[.62rem] font-semibold uppercase tracking-[.08em] ${settings.emailNotificationsEnabled ? 'bg-[#dce9e1] text-[#355f55]' : 'bg-[#ece9e2] text-[#746f67]'}`}>{settings.emailNotificationsEnabled ? 'On' : 'Off'}</span></div>
                    <p className="mt-2 text-xs leading-5 text-[#7b8580]">Automatically email the configured destination when a lead is submitted.</p>
                  </button>

                  <button type="button" aria-pressed={settings.whatsappNotificationsEnabled} onClick={() => setSettings((current) => ({ ...current, whatsappNotificationsEnabled: !current.whatsappNotificationsEnabled }))} className={`rounded-2xl border p-4 text-left transition ${settings.whatsappNotificationsEnabled ? 'border-[#557f73]/35 bg-[#edf3ef]' : 'border-[#33423c]/10 bg-[#f8f5ed]'}`}>
                    <div className="flex items-center justify-between gap-3"><span className="inline-flex items-center gap-2 text-sm font-semibold"><BellRing size={15} /> WhatsApp auto</span><span className={`rounded-full px-2.5 py-1 text-[.62rem] font-semibold uppercase tracking-[.08em] ${settings.whatsappNotificationsEnabled ? 'bg-[#dce9e1] text-[#355f55]' : 'bg-[#ece9e2] text-[#746f67]'}`}>{settings.whatsappNotificationsEnabled ? 'On' : 'Off'}</span></div>
                    <p className="mt-2 text-xs leading-5 text-[#7b8580]">Uses Meta WhatsApp Cloud API. It only sends when the server credentials are configured.</p>
                  </button>
                </div>

                <div className="rounded-2xl border border-[#33423c]/10 bg-[#f8f5ed] p-4 sm:flex sm:items-center sm:justify-between sm:gap-4">
                  <div><p className="inline-flex items-center gap-2 text-sm font-semibold"><MessageCircle size={15} /> Manual WhatsApp</p><p className="mt-1 text-xs leading-5 text-[#7b8580]">Open the configured WhatsApp number directly. This does not require Meta API credentials.</p></div>
                  <a href={whatsappHref(settings.whatsappNumber)} target="_blank" rel="noreferrer" className="mt-3 inline-flex items-center gap-2 rounded-xl bg-[#345b52] px-4 py-2.5 text-xs font-semibold text-white sm:mt-0">Open WhatsApp <ExternalLink size={13} /></a>
                </div>

                {settingsError && <p className="rounded-xl bg-[#f4e9e6] px-4 py-3 text-sm text-[#8b4c43]">{settingsError}</p>}
                <div className="flex items-center gap-4"><button type="submit" disabled={settingsBusy} className="btn-primary disabled:opacity-60"><Save size={14} /> {settingsBusy ? 'Saving…' : 'Save notification settings'}</button>{settingsSaved && <span className="text-xs font-semibold text-[#557f73]">Saved.</span>}</div>
              </div>
            </form>

            <aside className="grid gap-5 self-start">
              <div className="rounded-2xl border border-[#33423c]/9 bg-white p-6 shadow-sm"><ShieldCheck size={19} className="text-[#557f73]" /><h3 className="mt-5 text-sm font-semibold">Secure by design</h3><p className="mt-2 text-xs leading-6 text-[#77827c]">The admin session is HTTP-only and signed. Passwords, SMTP credentials and Meta API tokens never reach the browser.</p></div>
              <div className="rounded-2xl border border-[#33423c]/9 bg-white p-6 shadow-sm"><p className="text-xs font-semibold uppercase tracking-[.14em] text-[#89928e]">Delivery readiness</p><div className="mt-4 space-y-3 text-xs"><div className="flex justify-between gap-3"><span>Email SMTP</span><strong>{settings.delivery?.emailConfigured ? 'Configured' : 'Needs setup'}</strong></div><div className="flex justify-between gap-3"><span>WhatsApp API</span><strong>{settings.delivery?.whatsappConfigured ? 'Configured' : 'Needs setup'}</strong></div><div className="flex justify-between gap-3"><span>WhatsApp preference</span><strong>{settings.whatsappNotificationsEnabled ? 'Enabled' : 'Disabled'}</strong></div></div></div>
            </aside>

            <div className="rounded-2xl border border-[#33423c]/9 bg-white p-6 shadow-sm sm:p-8 xl:col-span-2">
              <div className="grid gap-8 lg:grid-cols-[.8fr_1.2fr]">
                <div><p className="text-xs font-semibold uppercase tracking-[.18em] text-[#668076]">Future setup</p><h3 className="mt-2 text-2xl font-medium tracking-[-.035em]">Enable automatic WhatsApp notifications later.</h3><p className="mt-3 text-sm leading-7 text-[#74807a]">The manual WhatsApp shortcut works now. Automatic lead alerts require Meta WhatsApp Cloud API credentials on the server. Keep secrets in <code>server/.env</code> locally or Hostinger Environment Variables in production — never in the browser or GitHub.</p></div>
                <div className="grid gap-4 text-sm text-[#59675f]">
                  <ol className="grid gap-3 pl-5 text-sm leading-6 [list-style:decimal]">
                    <li>Create a Meta developer app and add the WhatsApp product.</li>
                    <li>Add and verify the WhatsApp sending number in Meta's API setup.</li>
                    <li>Create an approved notification template with four body values in this order: name, email, company and message.</li>
                    <li>Add the credentials below to <code>server/.env</code> for local use, or to Hostinger Environment Variables for production.</li>
                    <li>Restart or redeploy the server. When “WhatsApp API” above shows <strong>Configured</strong>, switch “WhatsApp auto” on and save.</li>
                  </ol>
                  <pre className="overflow-x-auto rounded-2xl bg-[#273a34] p-4 text-xs leading-6 text-[#eef4f0]"><code>{`WHATSAPP_PHONE_NUMBER_ID=YOUR_META_PHONE_NUMBER_ID\nWHATSAPP_ACCESS_TOKEN=YOUR_META_ACCESS_TOKEN\nWHATSAPP_TEMPLATE_NAME=YOUR_APPROVED_TEMPLATE_NAME\nWHATSAPP_TEMPLATE_LANGUAGE=en`}</code></pre>
                  <p className="text-xs leading-6 text-[#7b8580]">Meta may charge for WhatsApp Business Platform messages according to its current pricing. The admin portal stores only the destination and on/off preference; API tokens stay server-side.</p>
                </div>
              </div>
            </div>
          </section>
        )}
      </main>
    </div>
  );
}
